import React, { useState } from 'react';
import { intersection, isEmpty } from 'lodash';

import { Heading } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import PlusIcon from '~/components/icons/PlusIcon';
import LoadingIndicator from '~/components/LoadingIndicator';
import useDataFetcher from '~/components/useDataFetcher';
import type { SlaRuleDTO } from '~/server_shared/openapi/data-contracts';

import CardDialog from '../../../CardDialog';
import { useSysconfig } from '../../SystemConfigurationScreen';

import { deleteSlaRule, setSlaRuleEnabled, slaRulesUrl } from './api/slaRulesApi';
import SlaRulesFilters from './components/SlaRulesFilters';
import SlaRulesTable from './components/SlaRulesTable';
import SlaRuleDialog from './RuleDialog';
import { TEXTS } from './texts';
import type { SlaRuleFilterParams, SlaRuleList, SysconfigOrganization } from './types';

const SlaEngine: React.FC = () => {
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const [isProcessing, setIsProcessing] = useState(false);
  // The rule the dialog edits, 'new' while it creates one, and null while it is closed.
  const [dialogRule, setDialogRule] = useState<SlaRuleDTO | 'new' | null>(null);
  const [filteredParams, setFilteredParams] = useState<SlaRuleFilterParams>({
    subOrgIds: [],
    lobs: [],
    entityLevel: 'all',
  });

  // Add, Edit and the toggle need WRITE, and delete FULL. The customer Operations tile only routes
  // here with FULL, but internal sysconfig mounts this screen with no scope gate, so a READ-only
  // holder can land here and would otherwise get live controls that all answer 403.
  const canWrite = useHasPermission({ action: PERMISSION_ACTIONS.SYSCONFIG_AUTOMATION, verb: PERMISSION_VERBS.WRITE });
  const canDelete = useHasPermission({ action: PERMISSION_ACTIONS.SYSCONFIG_AUTOMATION, verb: PERMISSION_VERBS.FULL });

  const {
    isLoading,
    isError,
    data: ruleList,
    reloadData: reloadRules,
  } = useDataFetcher<SlaRuleList>(slaRulesUrl(organization.id));

  const hasActiveFilters =
    !isEmpty(filteredParams.subOrgIds) || !isEmpty(filteredParams.lobs) || filteredParams.entityLevel !== 'all';

  // A rule that selects no sub-organization or LOB applies to all of them, so it survives any
  // choice in that filter.
  const filteredRules = (ruleList?.rules ?? [])
    .filter(
      (rule) =>
        isEmpty(filteredParams.subOrgIds) ||
        isEmpty(rule.sub_organization_ids) ||
        !isEmpty(intersection(rule.sub_organization_ids, filteredParams.subOrgIds))
    )
    .filter(
      (rule) =>
        isEmpty(filteredParams.lobs) || isEmpty(rule.lobs) || !isEmpty(intersection(rule.lobs, filteredParams.lobs))
    )
    .filter((rule) => filteredParams.entityLevel === 'all' || rule.entity_level === filteredParams.entityLevel);

  const withProcessing = async (fn: () => Promise<void>) => {
    setIsProcessing(true);
    try {
      await fn();
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete/enable are invoked fire-and-forget from the row (ThreeDotsMenu / Switch), so swallow
  // after the service has already reported the error — otherwise the rethrow from
  // `withProcessing` becomes an unhandled promise rejection.
  const handleDeleteRule = async (rule: SlaRuleDTO) => {
    try {
      await withProcessing(() => deleteSlaRule(organization.id, rule.id));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEnabledChanged = async (rule: SlaRuleDTO) => {
    try {
      await withProcessing(() => setSlaRuleEnabled(organization.id, rule.id, !rule.is_enabled));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  if (isLoading || isError || !ruleList) {
    return <LoadingIndicator isError={isError} />;
  }

  return (
    <CardDialog noCardTitle>
      <div className="p-20">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          {TEXTS.HEADING}
        </Heading>
        <AlertBanner
          className="mb-12"
          alertType={AlertBanner.ALERT_TYPES.INFO}
          title={TEXTS.ALERT_TITLE}
          note={TEXTS.RULE_ISSUES_DISCLAIMER(ruleList.entity_cap)}
          withIcon
        />
        {canWrite ? (
          <div className="flex justify-end pb-12">
            <Button
              color="primary"
              onClick={() => setDialogRule('new')}
              startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
            >
              {TEXTS.ADD_RULE}
            </Button>
          </div>
        ) : null}
        <SlaRulesFilters
          organization={organization}
          subOrgIds={filteredParams.subOrgIds}
          entityLevel={filteredParams.entityLevel}
          onSubOrgChanged={(subOrgIds) => setFilteredParams((p) => ({ ...p, subOrgIds }))}
          onLobsChanged={(lobs) => setFilteredParams((p) => ({ ...p, lobs }))}
          onEntityLevelChanged={(entityLevel) => setFilteredParams((p) => ({ ...p, entityLevel }))}
        />
        <SlaRulesTable
          organization={organization}
          rules={filteredRules}
          isProcessing={isProcessing}
          canWrite={canWrite}
          canDelete={canDelete}
          emptyStateText={hasActiveFilters ? TEXTS.NO_MATCHING_RULES : TEXTS.NO_RULES}
          onEditRule={setDialogRule}
          onDeleteRule={handleDeleteRule}
          onEnabledChanged={handleEnabledChanged}
        />
      </div>
      {dialogRule ? (
        <SlaRuleDialog
          organizationId={organization.id}
          entityCap={ruleList.entity_cap}
          rule={dialogRule === 'new' ? undefined : dialogRule}
          onCancel={() => setDialogRule(null)}
          onSaved={async () => {
            await reloadRules();
          }}
        />
      ) : null}
    </CardDialog>
  );
};

export default SlaEngine;
